import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Shuffle, Wand2, Loader2 } from 'lucide-react';
import { GENERATOR_CATEGORIES, TRANSLATIONS, API_URL } from '../../config';
import Gallery from '../gallery/Gallery';
import { apiClient } from '../../utils/apiClient';

export default function PromptGenerator({ wizardData, onBack, onBackToDashboard }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [selections, setSelections] = useState({
    aktion: null,        // Single
    koerper: null,       // Single
    kleidung: [],        // Multi
    szene: null,         // Single
    accessoires: [],     // Multi
    ansicht: null        // Single
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [generations, setGenerations] = useState([]);

  const handleCookieSettings = () => {
    window.dispatchEvent(new Event('openCookieSettings'));
  };

  const handleDeleteAccount = async () => {
    const confirmation = window.prompt(
      'WARNUNG: Diese Aktion löscht unwiderruflich deinen Account und ALLE deine Daten!\n\n' +
      'Bitte tippe "LÖSCHEN" ein, um fortzufahren:'
    );

    if (confirmation !== 'LÖSCHEN') {
      if (confirmation !== null) {
        alert('Löschung abgebrochen.');
      }
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem('snapshot_user'));
      const userId = user?.id;

      if (!userId) {
        alert('Fehler: User-ID nicht gefunden.');
        return;
      }

      const response = await apiClient.delete(`/api/auth/user/${userId}`, {
        user_id: userId
      });

      if (response.ok) {
        // Logout: localStorage clearen
        localStorage.removeItem('snapshot_token');
        localStorage.removeItem('snapshot_user');
        localStorage.removeItem('snapshot_referral_code');
        
        alert('Dein Account wurde erfolgreich gelöscht.');
        
        // Redirect zur Welcome-Seite
        window.location.href = '/welcome';
      } else {
        const error = await response.json();
        alert(`Fehler beim Löschen: ${error.detail || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      console.error('Delete account error:', error);
      alert('Fehler beim Löschen des Accounts. Bitte versuche es später erneut.');
    }
  };

  const handleSelect = (category, optionId) => {
    if (category === 'kleidung' || category === 'accessoires') {
      // Multi-Selection für Kleidung & Accessoires
      setSelections(prev => {
        const current = prev[category] || [];
        const isSelected = current.includes(optionId);
        return {
          ...prev,
          [category]: isSelected 
            ? current.filter(id => id !== optionId)  // Remove
            : [...current, optionId]                 // Add
        };
      });
    } else {
      // Single-Selection für Aktion, Körper, Szene, Ansicht
      setSelections(prev => ({
        ...prev,
        [category]: prev[category] === optionId ? null : optionId
      }));
    }
  };

  const handleRandomize = () => {
    const randomSelections = {};
    Object.keys(GENERATOR_CATEGORIES).forEach(cat => {
      const options = GENERATOR_CATEGORIES[cat];
      
      if (cat === 'kleidung' || cat === 'accessoires') {
        // Multi: 1-3 zufällige Optionen
        const count = Math.floor(Math.random() * 3) + 1;
        const shuffled = [...options].sort(() => 0.5 - Math.random());
        randomSelections[cat] = shuffled.slice(0, count).map(o => o.id);
      } else {
        // Single: 1 zufällige Option
        const randomOption = options[Math.floor(Math.random() * options.length)];
        randomSelections[cat] = randomOption.id;
      }
    });
    setSelections(randomSelections);
  };

  const buildPrompt = () => {
    if (customPrompt?.trim()) return customPrompt;

    const parts = [];
    Object.entries(selections).forEach(([category, value]) => {
      // Multi-Selection (Array)
      if (Array.isArray(value)) {
        value.forEach(optionId => {
          const option = GENERATOR_CATEGORIES[category].find(o => o.id === optionId);
          if (option) {
            const baseId = optionId.replace('_s', '');
            const translation = TRANSLATIONS[baseId];
            if (translation) parts.push(translation);
            if (option.sensual) parts.push('(sensual lighting)');
          }
        });
      }
      // Single-Selection (String)
      else if (value) {
        const option = GENERATOR_CATEGORIES[category].find(o => o.id === value);
        if (option) {
          const baseId = value.replace('_s', '');
          const translation = TRANSLATIONS[baseId];
          if (translation) parts.push(translation);
          if (option.sensual) parts.push('(sensual lighting)');
        }
      }
    });

    return parts.length > 0 
      ? `ohwx person ${parts.join(', ')}, professional photography, high quality`
      : '';
  };

  const handleGenerate = async () => {
    const prompt = buildPrompt();
    if (!prompt && !customPrompt?.trim()) return;

    const finalPrompt = customPrompt?.trim() || prompt;

    // Logging: generate:start
    console.log('🚀 generate:start', {
      prompt: finalPrompt,
      timestamp: new Date().toISOString()
    });

    setIsGenerating(true);
    setShowGallery(false);

    try {
      // Mock: 2-3s delay to simulate API
      await new Promise(resolve => setTimeout(resolve, 2500));
      
      // Mock generations
      const mockGenerations = Array(4).fill(null).map((_, i) => ({
        id: `gen_${Date.now()}_${i}`,
        prompt: finalPrompt,
        image_url: `https://placehold.co/512x512/10B981/000000?text=Avatar+${i+1}`,
        created_at: new Date().toISOString()
      }));
      
      // Logging: generate:done
      console.log('✅ generate:done (mock)', {
        images_count: mockGenerations.length,
        timestamp: new Date().toISOString()
      });
      
      setGenerations(prev => [...mockGenerations, ...prev]);
      
      // WICHTIG: Erst Gallery zeigen, DANN Banner schließen!
      setShowGallery(true);
      
      // Kurze Verzögerung, dann Banner schließen + ZURÜCK ZUM DASHBOARD
      setTimeout(() => {
        setIsGenerating(false);
        
        // Nach Generierung: Zurück zum Dashboard
        setTimeout(() => {
          if (onBackToDashboard) {
            console.log('🏠 Navigiere zurück zum Dashboard (Galerie dort sichtbar)');
            onBackToDashboard();
          }
        }, 1000);
      }, 100);
      
      // Logging: gallery:open
      console.log('🖼️ gallery:open', {
        images_count: mockGenerations.length,
        timestamp: new Date().toISOString()
      });
      
    } catch (error) {
      console.error('❌ Generation error:', error);
      alert(`Fehler bei der Generierung: ${error.message}`);
      setIsGenerating(false);
    }
  };

  const fetchGenerations = async () => {
    try {
      // AVATAR-GUARD: Need training_id from wizardData
      const trainingId = wizardData?.training_id || wizardData?.trainingJob?.id;
      
      if (!trainingId) {
        console.warn('⚠️ PromptGenerator: No training_id available, skipping gallery fetch');
        return;
      }
      
      const res = await apiClient.get(`/api/generations/list?avatar_id=${trainingId}`);
      const data = await res.json();
      setGenerations(data.generations || []);
    } catch (error) {
      console.error('Error fetching generations:', error);
    }
  };

  useEffect(() => {
    fetchGenerations();
  }, []);

  const CategorySection = ({ title, category, icon: Icon }) => {
    const isMulti = category === 'kleidung' || category === 'accessoires';
    
    return (
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          {Icon && <Icon className="w-5 h-5 text-[#10B981]" />}
          <h3 className="text-lg font-bold text-[#10B981]">{title}</h3>
          {isMulti && (
            <span className="text-xs text-white/50 font-normal">(Mehrfachauswahl möglich)</span>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
          {GENERATOR_CATEGORIES[category].map((option) => {
            const isSelected = isMulti 
              ? (selections[category] || []).includes(option.id)
              : selections[category] === option.id;
            
            return (
              <button
                key={option.id}
                onClick={() => handleSelect(category, option.id)}
                className={`px-4 py-2.5 rounded-lg font-medium text-xs transition-all relative ${
                  isSelected
                    ? 'bg-gradient-to-br from-[#10B981] to-[#059669] text-black glow-green border-2 border-[#10B981]'
                    : 'bg-black text-white/80 border border-white/20 hover:bg-white/5 hover:border-[#10B981]/40'
                }`}
              >
                {option.label}
                {option.sensual && (
                  <span className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${
                    isSelected ? 'bg-black text-[#10B981]' : 'bg-[#10B981] text-black'
                  }`}>
                    S
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const currentPrompt = buildPrompt();
  const hasAnyInput = customPrompt?.trim() || currentPrompt;

  return (
    <div className="bg-black grain-texture text-white min-h-screen py-12 px-4">
      
      {/* Hamburger Menu */}
      <button
        onClick={() => setMenuOpen(!menuOpen)}
        className="fixed top-6 right-6 z-50 p-3 hover:bg-white/10 rounded-lg transition-all"
        aria-label="Menu"
      >
        <div className="w-6 h-5 flex flex-col justify-between">
          <span className={`w-full h-0.5 bg-white transition-all ${menuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
          <span className={`w-full h-0.5 bg-white transition-all ${menuOpen ? 'opacity-0' : ''}`}></span>
          <span className={`w-full h-0.5 bg-white transition-all ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
        </div>
      </button>

      {/* Side Menu */}
      <div className={`fixed top-0 right-0 h-full w-80 bg-black/95 backdrop-blur-xl border-l border-white/10 transform transition-transform duration-300 z-40 ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <nav className="flex flex-col p-8 space-y-6 mt-20">
          <button 
            onClick={() => {
              setMenuOpen(false);
              if (onBackToDashboard) onBackToDashboard();
            }}
            className="text-white hover:text-[#10B981] transition-colors text-lg font-medium text-left"
          >
            🏠 Dashboard
          </button>
          <button 
            className="text-white/60 hover:text-white transition-colors text-sm text-left opacity-50 cursor-not-allowed"
            disabled
          >
            🎨 Avatar-Generator
          </button>
          <button 
            onClick={() => {
              setMenuOpen(false);
              localStorage.removeItem('snapshot_beta_access');
              window.location.href = '/welcome';
            }}
            className="text-white/60 hover:text-white transition-colors text-sm text-left"
          >
            🚪 Abmelden
          </button>
          <div className="border-t border-white/10 my-2"></div>
          <Link to="/was-ist-snapshot" className="text-white/60 hover:text-white transition-colors text-sm">
            Was ist SNAPSHOT
          </Link>
          <Link to="/impressum" className="text-white/60 hover:text-white transition-colors text-sm">
            Impressum
          </Link>
          <Link to="/datenschutz" className="text-white/60 hover:text-white transition-colors text-sm">
            Datenschutz
          </Link>
          <Link to="/agb" className="text-white/60 hover:text-white transition-colors text-sm">
            AGB
          </Link>
          <Link to="/kontakt" className="text-white/60 hover:text-white transition-colors text-sm">
            Kontakt
          </Link>
          <button onClick={handleCookieSettings} className="text-white/60 hover:text-white transition-colors text-sm text-left">
            Cookie-Einstellungen
          </button>
          <div className="border-t border-white/10 my-2"></div>
          <button 
            onClick={handleDeleteAccount}
            className="text-red-500/80 hover:text-red-500 transition-colors text-sm text-left font-semibold"
          >
            Account löschen
          </button>
        </nav>
      </div>

      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-3">
            Erstelle deinen <span className="text-[#10B981]">Avatar</span>
          </h1>
          <p className="text-white/70 text-lg mb-2">Wähle Stil, Pose und Szene</p>
          <p className="text-white/50 text-sm">62+ Optionen für einzigartige Ergebnisse</p>
          
          {/* Legende für S-Badge */}
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full">
            <div className="w-4 h-4 rounded-full bg-[#10B981] flex items-center justify-center text-[10px] font-bold text-black">
              S
            </div>
            <span className="text-white/60 text-xs">= Sinnliche Variante</span>
          </div>
        </div>

        {/* Custom Prompt Input */}
        <div className="mb-8 bg-black border-2 border-[#10B981]/20 rounded-2xl p-6 glow-green-soft">
          <label className="block text-base font-bold text-[#10B981] mb-3">
            Eigener Prompt (optional)
          </label>
          <div className="flex gap-3">
            <input
              type="text"
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="z.B. Eine Latina im Sommerkleid am Strand..."
              className="flex-1 px-4 py-3 bg-black border-2 border-white/30 rounded-xl text-white placeholder:text-white/40 focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/50 transition-all"
            />
            <button
              onClick={handleRandomize}
              className="px-5 py-3 bg-[#10B981]/10 border-2 border-[#10B981]/40 text-[#10B981] rounded-xl hover:bg-[#10B981]/20 transition-all font-bold"
              title="Zufälliger Prompt"
            >
              <Shuffle className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-white/50 mt-2">
            💡 Dein Prompt wird automatisch ins Englische übersetzt, um das beste KI-Ergebnis zu erzielen.
          </p>
        </div>

        {/* Live Prompt Preview */}
        {currentPrompt && (
          <div className="mb-8 p-4 bg-[#10B981]/10 border-2 border-[#10B981]/30 rounded-xl">
            <div className="flex items-start gap-2 mb-2">
              <Wand2 className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-bold text-[#10B981] mb-1">Aktueller Prompt (EN):</p>
                <p className="text-sm text-white/90 font-mono">{currentPrompt}</p>
              </div>
            </div>
          </div>
        )}

        {/* Categories */}
        <div className="bg-black border-2 border-white/10 rounded-2xl p-6 sm:p-8 mb-8">
          <CategorySection title="🧍‍♀️ Aktion" category="aktion" icon={null} />
          <CategorySection title="🧍‍♂️ Körper" category="koerper" icon={null} />
          <CategorySection title="👗 Kleidung" category="kleidung" icon={null} />
          <CategorySection title="🌅 Szene" category="szene" icon={null} />
          <CategorySection title="🕶 Accessoires" category="accessoires" icon={null} />
          <CategorySection title="🎥 Ansicht" category="ansicht" icon={null} />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={onBack}
            className="px-8 py-4 border-2 border-white/30 text-white rounded-xl hover:bg-white/5 hover:border-white/50 transition-all font-semibold"
          >
            Zurück
          </button>
          <button
            onClick={handleGenerate}
            disabled={!hasAnyInput || isGenerating}
            className={`flex-1 px-8 py-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
              hasAnyInput && !isGenerating
                ? 'bg-gradient-to-br from-[#10B981] to-[#059669] text-black hover:from-[#059669] hover:to-[#10B981] glow-green pulse-glow shadow-lg'
                : 'bg-white/10 text-white/40 cursor-not-allowed border-2 border-white/20'
            }`}
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Wird generiert...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Bild generieren
              </>
            )}
          </button>
        </div>

        {/* Generating Banner */}
        {isGenerating && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0, 0, 0, 0.95)' }}>
            <div className="bg-black border-2 border-[#10B981]/40 rounded-2xl p-8 max-w-md w-full text-center glow-green-soft">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center glow-green">
                <Sparkles className="w-8 h-8 text-black animate-pulse" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Deine Bilder entstehen jetzt</h3>
              <p className="text-white/70 text-sm leading-relaxed mb-4">
                Bitte einen Moment Geduld – wir holen deine 4 Bilder in deine Galerie. 
                Das dauert nur kurz, danach kannst du sofort weitermachen.
              </p>
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-[#10B981] rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-[#10B981] rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-[#10B981] rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        )}

        {/* Gallery Section */}
        {showGallery && generations.length > 0 && (
          <div className="mt-12 pt-8 border-t-2 border-white/10" data-gallery-section>
            <h2 className="text-3xl font-bold text-white mb-6 text-center">
              Deine <span className="text-[#10B981]">Galerie</span>
            </h2>
            <Gallery generations={generations} loading={false} />
          </div>
        )}
      </div>
    </div>
  );
}
